import React, { memo, useCallback, useMemo, useState } from 'react';
import Skeleton from '@material-ui/lab/Skeleton';

// mui components
import { makeStyles, withStyles } from '@material-ui/core/styles';
import MuiTable from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Tooltip from '@material-ui/core/Tooltip';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';

// custom imports
import { sort, ColumnType, Order, SortBy, isHiddenSort } from '../../../utils/sortBy';
import { withSearchIndex, search } from '../../../utils/searchBy';

export {
  ColumnType,
  Order,
  SortBy,
  sort,
  isHiddenSort,
  withSearchIndex,
  search
};

const useStyles = makeStyles({
  table: {
    zIndex: 1,
    minWidth: '100%',
    position: 'relative'
  }
});

const StyledRegularCondensedTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: theme.palette.common.white,
    padding: '8px 4px',
    boxSizing: 'border-box',
    fontSize: 13,
    fontWeight: 600
  },
  body: {
    fontSize: 13,
    padding: '8px 4px',
    boxSizing: 'border-box'
  },
}))(TableCell);

const StyledGroupHeadingCondensedTableCell = withStyles((theme) => ({
  head: {
    padding: '8px 4px',
    boxSizing: 'border-box',
    fontSize: 13,
    fontWeight: 600
  },
  body: {
    color: '#fff',
    fontSize: 13,
    padding: '8px 4px',
    boxSizing: 'border-box',
    fontWeight: 600
  },
}))(TableCell);

const StyledRegularTableCell = withStyles((theme) => ({
  body: {
    fontSize: 13,
    padding: '18px 4px',
    boxSizing: 'border-box'
  },
}))(TableCell);

const PatientChartHeaderTableCell = withStyles((theme) => ({
  head: {
    padding: '8px 4px',
    boxSizing: 'border-box',
    fontSize: 14,
    backgroundColor: '#EBEBEB',
    color: '#000000'
  },
}))(TableCell);

const StyledBolderCondensedTableCell = withStyles((theme) => ({
  body: {
    fontWeight: 600,
    fontSize: 14,
    padding: '8px 4px',
    boxSizing: 'border-box'
  },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
  root: {
    cursor: 'pointer',

    '&:hover': {
      backgroundColor: '#FAFAFA',
    },
  },
}))(TableRow);

const StyledTableRowWithLightBackground = withStyles(() => ({
  root: {
    cursor: 'pointer',
    backgroundColor: '#FAFAFA',
  },
}))(TableRow);

const StyledTableGroupHeading = withStyles((theme) => ({
  root: {
    backgroundColor: '#02ac4f'
  },
}))(TableRow);

export function createColumn({ key, label, type = ColumnType.string, align = 'left', hideSort = false, width = 'auto', tooltip = '', action = false, }) {
  return { key, label, type, align, hideSort, width, tooltip, action };
}

const Columns = [
  createColumn({ key: 'dessert', label: 'Dessert (100g serving)', type: ColumnType.string }),
  createColumn({ key: 'calories', label: 'Calories', align: 'right', type: ColumnType.numeric }),
  createColumn({ key: 'fat', label: 'Fat (g)', align: 'right', type: ColumnType.numeric }),
  createColumn({ key: 'carbs', label: 'Carbs (g)', align: 'right', type: ColumnType.numeric }),
  createColumn({ key: 'protein', label: 'Protein (g)', align: 'right', type: ColumnType.numeric }),
  createColumn({ key: 'dose', label: 'Dosage', type: ColumnType.jsx, hideSort: true }),
];

const Rows = [
  { key: 1, dessert: 'Frozen yoghurt', calories: 159, fat: 6.0, carbs: 24, protein: 4.0, dose: <div>Please do not take it.</div> },
  { key: 2, dessert: 'Ice cream sandwich', calories: 237, fat: 9.0, carbs: 37, protein: 4.3 },
  { key: 3, dessert: 'Eclair', calories: 262, fat: 16.0, carbs: 24, protein: 6.0 },
  { key: 4, dessert: 'Cupcake', calories: 305, fat: 3.7, carbs: 67, protein: 4.3 },
  { key: 5, dessert: 'Gingerbread', calories: 356, fat: 16.0, carbs: 49, protein: 3.9 }
];

const LoadingRows = [
  {
    key: 1,
  },
  {
    key: 2,
  }
];

export const SortIcon = new Map([
  [Order.asc, ArrowDropUpIcon],
  [Order.desc, ArrowDropDownIcon],
]);

const LoadingRow = ({
  row = {},
  columns = [],
}) => {
  return (
    <StyledTableRow key={row.key}>
      {
        columns.map(({ key, align }) => (
          <StyledRegularCondensedTableCell
            key={key}
            align={align}
          >
            <Skeleton variant='text' />
          </StyledRegularCondensedTableCell>
        ))
      }
    </StyledTableRow>
  );
};

const RegularRow = ({
  row = {},
  columns = [],
  handler = {
    onRowClick: () => {}
  }
}) => (
  <StyledTableRow key={row.key} onClick={handler.onRowClick.bind(this, row)}>
    {
      columns.map(({ key, align }) => (
        <StyledRegularTableCell
          key={key}
          align={align}
        >
          {row[key]}
        </StyledRegularTableCell>
      ))
    }
  </StyledTableRow>
);

const RegularRowWithLightBackground = ({
  row = {},
  columns = [],
  handler = {
    onRowClick: () => {}
  }
}) => (
  <StyledTableRowWithLightBackground key={row.key} onClick={handler.onRowClick.bind(this, row)}>
    {
      columns.map(({ key, align }) => (
        <StyledRegularTableCell
          key={key}
          align={align}
        >
          {row[key]}
        </StyledRegularTableCell>
      ))
    }
  </StyledTableRowWithLightBackground>
);

const RegularCondensedRow = ({
  row = {},
  columns = [],
  handler = {
    onRowClick: () => {}
  }
}) => (
  <StyledTableRow key={row.key} onClick={handler.onRowClick.bind(this, row)}>
    {
      columns.map(({ key, align }) => (
        <StyledRegularCondensedTableCell
          key={key}
          align={align}
        >
          {row[key]}
        </StyledRegularCondensedTableCell>
      ))
    }
  </StyledTableRow>
);

const BolderCondensedRow = ({
  row = {},
  columns = [],
  handler = {
    onRowClick: () => {}
  }
}) => (
  <StyledTableRow key={row.key} onClick={handler.onRowClick.bind(this, row)}>
    {
      columns.map(({ key, align }) => (
        <StyledBolderCondensedTableCell
          key={key}
          align={align}
        >
          {row[key]}
        </StyledBolderCondensedTableCell>
      ))
    }
  </StyledTableRow>
);

const GroupHeading = ({
  row = {},
  columns = []
}) => {
  return (
    <StyledTableGroupHeading key={row.key}>
      <StyledGroupHeadingCondensedTableCell
        colSpan={columns.length}
      >
        {row.label}
      </StyledGroupHeadingCondensedTableCell>
    </StyledTableGroupHeading>
  );
};

export const RowTypeConstant = {
  loading: 'loading',
  regular: 'regular',
  regularWithLightBackground: 'regularWithLightBackground',
  regularCondensed: 'regularCondensed',
  groupHeading: 'groupHeading',
  bolderCondensed: 'bolderCondensed',
};

export const RowType = new Map([
  [RowTypeConstant.loading, LoadingRow],
  [RowTypeConstant.regular, RegularRow],
  [RowTypeConstant.regularCondensed, RegularCondensedRow],
  [RowTypeConstant.regularWithLightBackground, RegularRowWithLightBackground],
  [RowTypeConstant.groupHeading, GroupHeading],
  [RowTypeConstant.bolderCondensed, BolderCondensedRow],
]);

export const HeaderCellTypeConstant = {
  v1: 'v1',
  v2: 'v2'
};

export const HeaderCellType = new Map([
  [HeaderCellTypeConstant.v1, StyledRegularCondensedTableCell],
  [HeaderCellTypeConstant.v2, PatientChartHeaderTableCell]
]);

export const Header = ({
  columns = [],
  sortBy = {},
  onSorting: createSortHandler = () => {},
  cellVersion = HeaderCellTypeConstant.v1,
}) => {
  const HeaderCell = HeaderCellType.get(cellVersion);
  return (
    <TableRow>
      {
        columns.map(({ key, label, align, type, hideSort, width = 'auto', tooltip }) => {
          const noSort = isHiddenSort({ type, hideSort });
          const SortHandlerCollection = new Map([
            [true, () => {}],
            [false, createSortHandler],
          ]);
          const handleSorting = SortHandlerCollection.get(noSort);

          return (
            <HeaderCell
              key={key}
              align={align}
              width={width}
            >
              <Tooltip title={tooltip} placement="top" arrow>
                <TableSortLabel
                  active={!noSort && sortBy.key === key}
                  direction={!noSort && sortBy.key === key ? sortBy.order : Order.asc}
                  onClick={handleSorting(key, type)}
                  hideSortIcon={noSort}
                >
                  {label}
                </TableSortLabel>
              </Tooltip>
            </HeaderCell>
          );
        })
      }
    </TableRow>
  );
};

export const Table = ({
  columns = Columns,
  rows: _rows = Rows,
  sortBy: _sortBy = SortBy,
  filterBy: _filterBy = '',
  stickyHeader: _stickyHeader = false,
  headerVersion: _headerVersion = HeaderCellTypeConstant.v1,
  loading: _loading = false,
  loadingRows: _loadingRows = LoadingRows,
  handler = {
    onRowClick: () => {}
  }
}) => {
  const classes = useStyles();
  const [sortBy, setSortBy] = useState(_sortBy);

  const columnMap = useMemo(() => {
    const map = new Map();
    columns.forEach(({ key, ...rest }) => map.set(key, rest));
    return map;
  }, [columns]);

  /**
   * @description prepare rows with searchIndex to perform keyword search
   */
  const rows = useMemo(() => {
    return withSearchIndex(_rows, columnMap);
  }, [_rows, columnMap]);

  /**
   * @description performs a keyword search
   */
  const filteredRows = useMemo(() => {
    const filterBy = _filterBy.toLowerCase().trim();
    if (filterBy === '') return rows;
    return search(rows, filterBy);
  }, [_filterBy, rows]);

  /**
   * @description sorts all the sortable columns
   */
  const sortedRows = useMemo(() => {
    if (sortBy.key === '') return filteredRows;
    return sort(filteredRows, sortBy.key, sortBy.order, sortBy.type);
  }, [filteredRows, sortBy]);

  /**
   * @description sets the sort parameters
   */
  const createSortHandler = useCallback((key, type) => (_) => {
    const nextSortBy = { key, order: Order.asc, type };
    if (key === sortBy.key) {
      nextSortBy.order = (sortBy.order === Order.asc) ? Order.desc : Order.asc;
    }
    setSortBy(nextSortBy);
  }, [sortBy]);

  const head = useMemo(() => {
    return (
      <Header
        columns={columns}
        sortBy={sortBy}
        onSorting={createSortHandler}
        cellVersion={_headerVersion}
      />
    );
  }, [createSortHandler, columns, _headerVersion]);

  const body = useMemo(() => {
    if (_loading) {
      return _loadingRows.map((row) => {
        const Row = RowType.get(RowTypeConstant.loading);
        return <Row key={row.key} row={row} columns={columns} />;
      });
    }

    return sortedRows.map((row) => {
      const Row = RowType.get(row.type || RowTypeConstant.regularCondensed);
      return <Row key={row.key} row={row} columns={columns} handler={handler} />;
    });
  }, [_loading, head, sortedRows]);

  return (
    <TableContainer>
      <MuiTable stickyHeader={_stickyHeader} className={classes.table} aria-label="orders" size="small">
        <TableHead>{head}</TableHead>
        <TableBody>{body}</TableBody>
      </MuiTable>
    </TableContainer>
  );
};

export default memo(Table);
